import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, MousePointerClick, Users, TrendingUp, RefreshCw, Loader2, ExternalLink, Calendar } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, sub, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  };
  return (
    <Card className="border border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-[#0F172A] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
          </div>
          <div className={`p-2 rounded-lg ${colors[color]}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function AnalyticsDashboard({ token }) {
  const [summary, setSummary] = useState(null);
  const [pageviews, setPageviews] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, pvRes, clRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { headers }),
        axios.get(`${API}/analytics/pageviews`, { headers, params: { days } }),
        axios.get(`${API}/analytics/clicks`, { headers, params: { days } }),
      ]);
      setSummary(sumRes.data);
      setPageviews(pvRes.data);
      setClicks(clRes.data);
    } catch (err) {
      console.error("Analytics fetch error:", err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        <span className="ml-2 text-sm text-slate-500">Loading analytics...</span>
      </div>
    );
  }

  const dailyData = pageviews?.daily?.map(d => ({
    ...d,
    date: d.date.slice(5),
  })) || [];

  const pageData = pageviews?.pages || [];

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[#0F172A]" />
          <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Site Analytics
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[140px] text-xs h-8" data-testid="analytics-days-select">
              <Calendar className="h-3 w-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} className="text-xs h-8 rounded-full" data-testid="analytics-refresh-btn">
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Eye} label="Total Page Views" value={summary.total_views.toLocaleString()} sub={`${summary.today_views} today`} color="blue" />
          <StatCard icon={Users} label="Unique Visitors" value={summary.unique_total.toLocaleString()} sub={`${summary.unique_today} today`} color="emerald" />
          <StatCard icon={MousePointerClick} label="Link Clicks" value={summary.total_clicks.toLocaleString()} sub={`${summary.today_clicks} today`} color="amber" />
          <StatCard icon={TrendingUp} label="This Week" value={summary.week_views.toLocaleString()} sub={`${summary.unique_week} unique visitors`} color="violet" />
        </div>
      )}

      {/* Traffic Over Time Chart */}
      {dailyData.length > 0 && (
        <Card className="border border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm text-[#0F172A] uppercase tracking-wider mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Traffic Over Time
            </h3>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="views" stroke="#0F172A" strokeWidth={2} dot={{ r: 3 }} name="Page Views" />
                  <Line type="monotone" dataKey="unique" stroke="#D4A843" strokeWidth={2} dot={{ r: 3 }} name="Unique Visitors" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Visited Pages */}
        {pageData.length > 0 && (
          <Card className="border border-slate-200">
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm text-[#0F172A] uppercase tracking-wider mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Most Visited Pages
              </h3>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pageData} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                    <YAxis dataKey="page" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={120} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#0F172A" radius={[0, 4, 4, 0]} name="Views" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Clicked Links */}
        <Card className="border border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm text-[#0F172A] uppercase tracking-wider mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Most Clicked Links
            </h3>
            {clicks?.top_links?.length > 0 ? (
              <div className="space-y-2 max-h-[280px] overflow-y-auto">
                {clicks.top_links.map((link, i) => (
                  <div key={i} data-testid={`click-item-${i}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="text-sm font-medium text-[#0F172A] truncate">
                        {link.label || link.url}
                      </p>
                      {link.label && (
                        <p className="text-xs text-slate-400 truncate mt-0.5 flex items-center gap-1">
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          {link.url}
                        </p>
                      )}
                    </div>
                    <Badge variant="secondary" className="text-xs shrink-0">{link.count} clicks</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm text-center py-8">No link clicks recorded yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Empty state */}
      {(!summary || summary.total_views === 0) && (
        <Card className="border border-slate-200">
          <CardContent className="p-10 text-center">
            <Eye className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No analytics data yet. Page views and link clicks will appear here as visitors browse your site.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
